import { DECISION_MAP } from 'app/modules/rules/constants';
import { Filter, FilterOption } from 'app/modules/filters/models';
import { FILTER_OPERATOR, FILTER_TYPE } from 'app/modules/filters/constants';
import { RtrDecision } from 'app/modules/rules/models';
import { ListFlowValidationResultsPayload } from 'app/modules/ruleFlows/requests';
import { txnEventExternalId } from 'app/shared/types/eventExternalIds';

export const VALIDATION_RESULTS_FILTER_KEYS = {
  DECISION: 'decision',
  DECISION_SOURCE: 'decision_source',
  EVENT_ID: 'event_id',
  GENERATES_ALERT: 'generates_alert',
};

export const VALIDATION_RESULTS_FILTER_OPTIONS: FilterOption[] = [
  {
    key: VALIDATION_RESULTS_FILTER_KEYS.DECISION,
    label: 'Decision',
    type: FILTER_TYPE.MULTI_SELECT,
    customize: {
      [FILTER_OPERATOR.IS_ONE_OF]: {
        inputProps: {
          options: [
            { text: DECISION_MAP[RtrDecision.ALLOW], value: RtrDecision.ALLOW },
            { text: DECISION_MAP[RtrDecision.DENY], value: RtrDecision.DENY },
            {
              text: DECISION_MAP[RtrDecision.STEP_UP_AUTHENTICATION],
              value: RtrDecision.STEP_UP_AUTHENTICATION,
            },
            { text: 'Error', value: 'error' },
          ],
        },
      },
      [FILTER_OPERATOR.IS_NOT_ONE_OF]: { disabled: true },
    },
  },
  {
    key: VALIDATION_RESULTS_FILTER_KEYS.DECISION_SOURCE,
    label: 'Decision source',
    type: FILTER_TYPE.MULTI_SELECT,
    customize: {
      [FILTER_OPERATOR.IS_ONE_OF]: {
        inputProps: { creatable: true, options: [] },
      },
      [FILTER_OPERATOR.IS_NOT_ONE_OF]: { disabled: true },
    },
  },
  {
    key: VALIDATION_RESULTS_FILTER_KEYS.EVENT_ID,
    label: 'Event ID',
    type: FILTER_TYPE.MULTI_SELECT,
    customize: {
      [FILTER_OPERATOR.IS_ONE_OF]: {
        inputProps: { creatable: true, options: [] },
      },
      [FILTER_OPERATOR.IS_NOT_ONE_OF]: { disabled: true },
    },
  },
  {
    key: VALIDATION_RESULTS_FILTER_KEYS.GENERATES_ALERT,
    label: 'Generates alert',
    type: FILTER_TYPE.SELECT,
    customize: {
      [FILTER_OPERATOR.IS]: {
        inputProps: {
          options: [
            { text: 'Yes', value: 'true' },
            { text: 'No', value: 'false' },
          ],
        },
      },
    },
  },
];

const filterNumbers = (array: (number | string)[]): number[] => {
  return array.reduce<number[]>((acc, i) => {
    const num = Number(i);
    if (!Number.isNaN(num)) {
      acc.push(num);
    }
    return acc;
  }, []);
};

const filterStrings = (array: (number | string)[]): string[] => {
  return array.filter((i): i is string => typeof i === 'string');
};

export const createValidationResultsFilterPayload = (filters: Filter[]) => {
  return filters.reduce<
    Omit<
      ListFlowValidationResultsPayload,
      | 'limit'
      | 'offset'
      | 'sort_direction'
      | 'sort_column'
      | 'flow_validation_id'
    >
  >((acc, i) => {
    const { key, operator, value } = i;
    switch (key) {
      case VALIDATION_RESULTS_FILTER_KEYS.DECISION: {
        if (operator === FILTER_OPERATOR.IS_ONE_OF) {
          acc.decisions = filterStrings(value);
        }
        break;
      }
      case VALIDATION_RESULTS_FILTER_KEYS.EVENT_ID: {
        if (operator === FILTER_OPERATOR.IS_ONE_OF) {
          // Brand as txn at this boundary; filter accepts the union shape.
          acc.event_external_ids = filterStrings(value).map(txnEventExternalId);
        }
        break;
      }
      case VALIDATION_RESULTS_FILTER_KEYS.DECISION_SOURCE: {
        if (operator === FILTER_OPERATOR.IS_ONE_OF) {
          acc.decision_source_rule_ids = filterNumbers(value);
        }
        break;
      }
      case VALIDATION_RESULTS_FILTER_KEYS.GENERATES_ALERT: {
        acc.generates_alert = value === 'true';
        break;
      }
      default:
        break;
    }
    return acc;
  }, {});
};
